import apiClient from "./api/client";
import ENDPOINTS from "./api/endpoints";

// Get full URL for logo (prepend API base URL if relative path)
const getLogoFullUrl = (logoUrl) => {
  if (!logoUrl) return '';
  if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
    return logoUrl;
  }
  const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8081';
  return `${apiBaseUrl}${logoUrl}`;
};

/**
 * Branch Service - handles fetching branch details
 * Used to get branch name, address, GSTIN, etc.
 */
const branchService = {
  /**
   * Get current user's branch details
   * @returns {Promise<Object>} Branch details including addresses
   */
  getCurrentBranch: async () => {
    try {
      const response = await apiClient.get(ENDPOINTS.BRANCH.GET_CURRENT);
      return response;
    } catch (error) {
      console.error("Failed to fetch current branch:", error);
      throw error;
    }
  },

  /**
   * Get branch by ID
   * @param {string} id - Branch ID
   * @returns {Promise<Object>} Branch details
   */
  getBranchById: async (id) => {
    try {
      const response = await apiClient.get(ENDPOINTS.BRANCH.GET_BY_ID(id));
      return response;
    } catch (error) {
      console.error("Failed to fetch branch by ID:", error);
      throw error;
    }
  },

  /**
   * Get all branches
   * @returns {Promise<Array>} List of branches
   */
  getAllBranches: async () => {
    try {
      const response = await apiClient.get(ENDPOINTS.BRANCH.GET_ALL);
      return response;
    } catch (error) {
      console.error("Failed to fetch branches:", error);
      throw error;
    }
  },

  /**
   * Update branch details
   * @param {string} id - Branch ID
   * @param {Object} data - Branch data to update
   * @returns {Promise<Object>} Updated branch
   */
  updateBranch: async (id, data) => {
    try {
      const response = await apiClient.put(ENDPOINTS.BRANCH.UPDATE(id), data);
      return response;
    } catch (error) {
      console.error("Failed to update branch:", error);
      throw error;
    }
  },

  /**
   * Format branch data for receipt display
   * Extracts relevant fields and formats address
   * @param {Object} branch - Raw branch data from API
   * @returns {Object} Formatted branch data for receipts
   */
  formatBranchForReceipt: (branch) => {
    if (!branch) return null;

    // Format address from addresses array
    let formattedAddress = '';
    if (branch.addresses && branch.addresses.length > 0) {
      const addr = branch.addresses[0]; // Use first address
      const parts = [];
      if (addr.address1) parts.push(addr.address1);
      if (addr.address2) parts.push(addr.address2);
      if (addr.city) parts.push(addr.city);
      if (addr.state) parts.push(addr.state);
      if (addr.zipcode) parts.push(addr.zipcode);
      formattedAddress = parts.join(', ');
    }

    return {
      name: branch.name || '',
      address: formattedAddress,
      phone: branch.contact || '',
      email: branch.email || '',
      gstNumber: branch.gstNumber || '',
      logoUrl: getLogoFullUrl(branch.logoUrl),
    };
  },
};

export default branchService;
